import { BehaviorSubject } from 'rxjs';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  OnChanges,
  Output,
  ViewChild,
  DoCheck,
  ElementRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { AutocompleteModel } from '../../../core/models/autocomplete.model';
import { UtilService } from '../../utils/util.service';

@Component({
  selector: 'mat-select-autocomplete',
  templateUrl: './select-autocomplete.component.html',
  styleUrls: ['./select-autocomplete.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SelectAutocompleteComponent implements OnInit, OnChanges, DoCheck {
  /** Translation key (or plain text) for the search box placeholder */
  @Input() selectPlaceholder = 'filters.search';
  @Input() placeholder: string;
  @Input() options: any[];
  @Input() disabledFlag = false;
  @Input() display = 'display';
  @Input() value = 'value';
  @Input() defaultValues: any[];
  @Input() formControl: UntypedFormControl = new UntypedFormControl();
  @Input() errorMsg = 'Field is required';
  @Input() showErrorMsg = false;
  @Input() selectedOptions: any[];
  @Input() multiple = true;
  @Input() selectAll = false;
  @Input() name: string;
  @Input() htmlOnlyRequired = false;

  // New Options
  @Input() labelCount = 1;
  @Input() appearance: 'fill' | 'outline' = 'outline';

  // Bind Filter Input
  @Input()
  public filterInput: BehaviorSubject<string>;

  @Output()
  selectionChange: EventEmitter<any> = new EventEmitter();
  @Output()
  openedChange: EventEmitter<any> = new EventEmitter();
  @Output()
  clear: EventEmitter<any> = new EventEmitter();

  @ViewChild('selectElem') selectElem: { toggle: () => void };
  @ViewChild('textInput') textInput: ElementRef;

  searchInput: string = '';
  timeout: any = null;

  filteredOptions: Array<any> = [];
  selectedValue: Array<any> = [];
  selectAllChecked = false;
  displayString = '';

  private baseOptions: AutocompleteModel[] = [];
  private manualOptions: AutocompleteModel[] = [];

  constructor(private utilService: UtilService) {
    if (!this.filterInput)
      this.filterInput = new BehaviorSubject('');
  }

  ngOnInit(): void {
    this.baseOptions = [...(this.options || [])];
    this.manualOptions = [];
    this.options = [...this.baseOptions, ...this.manualOptions];
    this.filterItem('');
  }

  ngOnChanges() {
    if (this.disabledFlag) {
      this.formControl.disable();
    } else {
      this.formControl.enable();
    }

    // whenever parent changes options, refresh base options and reset manual list
    this.baseOptions = [...(this.options || [])];
    this.manualOptions = [];
    this.options = [...this.baseOptions, ...this.manualOptions];

    this.filteredOptions = this.options;

    if (this.selectedOptions) {
      this.selectedValue = this.selectedOptions;
    } else if (this.formControl.value) {
      this.selectedValue = this.formControl.value;
    }
  }

  ngDoCheck() {
    if (this.selectedValue && !this.selectedValue.length) {
      this.selectionChange.emit(this.selectedValue);
    }
  }

  getFormFieldAppearance() {
    return this.appearance;
  }

  toggleDropdown() {
    this.selectElem?.toggle();
  }

  toggleSelectAll(val: { checked: any }) {
    if (val.checked) {
      this.filteredOptions.forEach((option) => {
        if (!this.selectedValue.includes(option[this.value])) {
          this.selectedValue = this.selectedValue.concat([option[this.value]]);
        }
      });
    } else {
      const filteredValues = this.getFilteredOptionsValues();
      this.selectedValue = this.selectedValue.filter(
        (item) => !filteredValues.includes(item)
      );
    }
    this.selectionChange.emit(this.selectedValue);
  }

  filterItem(value: string, newOption?: boolean, selectValue?: any) {
    this.filterInput.next(value);
    this.filteredOptions = this.options.filter(
      (item) =>
        item[this.display].toLowerCase().indexOf(value.toLowerCase()) > -1
    );
    this.selectAllChecked = true;
    this.filteredOptions.forEach((item) => {
      if (
        this.selectedValue &&
        !this.selectedValue.includes(item[this.value])
      ) {
        this.selectAllChecked = false;
      }
    });
    if (!this.filteredOptions.length) {
      this.selectAllChecked = false;
    }

    if (newOption && selectValue != null) {
      if (this.multiple) {
        const arr = Array.isArray(this.selectedValue) ? this.selectedValue : [];
        if (!arr.includes(selectValue)) {
          this.selectedValue = [...arr, selectValue];
        }
        this.formControl.setValue(this.selectedValue);
      } else {
        this.selectedValue = selectValue;
        this.formControl.setValue(selectValue);
      }

      this.selectionChange.emit(this.formControl.value);
      this.toggleDropdown();
    }
  }

  hideOption(option: any) {
    return !(this.filteredOptions.indexOf(option) > -1);
  }

  // Returns plain strings array of filtered values
  getFilteredOptionsValues() {
    const filteredValues: any[] = [];
    this.filteredOptions.forEach((option) => {
      filteredValues.push(option.value);
    });
    return filteredValues;
  }

  onDisplayString() {
    this.displayString = '';
    if (this.selectedValue && this.selectedValue.length) {
      let displayOption = [];
      if (this.multiple) {
        // Multi select display
        for (let i = 0; i < this.labelCount; i++) {
          displayOption[i] = this.options.filter(
            (option) => option[this.value] === this.selectedValue[i]
          )[0];
        }
        if (displayOption.length) {
          for (let i = 0; i < displayOption.length; i++) {
            if (displayOption[i] && displayOption[i][this.display]) {
              this.displayString += displayOption[i][this.display] + ',';
            }
          }
          this.displayString = this.displayString.slice(0, -1);
          if (
            this.selectedValue.length > 1 &&
            this.selectedValue.length > this.labelCount
          ) {
            this.displayString += ` (+${this.selectedValue.length - this.labelCount} others)`;
          }
        }
      } else {
        // Single select display
        displayOption = this.options.filter(
          (option) => option[this.value] === this.selectedValue
        );
        if (displayOption.length) {
          this.displayString = displayOption[0][this.display];
        }
      }
    }
    return this.displayString;
  }

  onSelectionChange(val: { value: any[] }) {
    const filteredValues = this.getFilteredOptionsValues();
    let count = 0;
    if (this.multiple) {
      this.selectedValue.filter((item) => {
        if (filteredValues.includes(item)) {
          count++;
        }
      });
      this.selectAllChecked = count === this.filteredOptions.length;
    }
    this.selectedValue = val.value;
    this.selectionChange.emit(this.selectedValue);
  }

  onOpenedChange(event: any) {
    this.openedChange.emit(event);
    this.textInput.nativeElement.focus();
  }

  clearSearch() {
    this.filterItem('');
    this.searchInput = '';
    if (this.multiple) {
      this.selectedValue = this.defaultValues || [];
    } else {
      this.formControl.setValue(null)
      this.selectedValue = this.defaultValues[0] || '';
    }
    this.clear.emit();
  }

  triggerSearch() {
    const input = this.utilService.removeDiacritics(this.searchInput || '');
    this.searchInput = input;

    this.filterItem(this.searchInput);

    clearTimeout(this.timeout);
    this.timeout = null;

    const key = this.norm(input);
    const exists = [...(this.baseOptions || []), ...(this.manualOptions || [])]
      .some(o => this.norm(o.display) === key);

    if (this.name !== 'state' && key && !exists) {
      this.timeout = setTimeout(() => this.addOption(this.searchInput), 2000);
    }
  }

  addOption(searchInput: string) {
    const input = (searchInput || '').trim();
    if (!input) {
      this.filterItem('', false);
      return;
    }

    // do not add manual searchInput if there are matches
    if (this.filteredOptions && this.filteredOptions.length > 0) {
      this.filterItem(input, false);
      return;
    }

    const existsInBase = this.baseOptions.some(o => (o.display || '').toLowerCase() === input.toLowerCase());
    const existsInManual = this.manualOptions.some(o => (o.display || '').toLowerCase() === input.toLowerCase());

    let newOption = false;
    let addedValue: any = null;

    if (this.name !== 'state' && !existsInBase && !existsInManual) {
      const manualOpt = { value: input, display: input, manual: true } as AutocompleteModel;
      this.manualOptions = [...this.manualOptions, manualOpt];
      this.options = [...this.baseOptions, ...this.manualOptions];

      newOption = true;
      addedValue = manualOpt.value;
    }

    this.filterItem(input, newOption, addedValue);
  }

  private norm(s: string): string {
    return this.utilService.removeDiacritics((s || '').trim()).toLowerCase();
  }
}
